/**
 * 设备命令构造器
 * 对齐旧版 js/server-1.js 中的命令协议
 *
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 * - 帧头: AA 53
 * - 长度: 从命令字到校验和的字节数
 * - 命令字: 1字节
 * - 数据: 变长
 * - 校验和: 帧头+长度+命令+数据的和，取反+1，取低8位
 */

import { hexStringToBytes } from './protocol'

/**
 * 设备命令代码
 * 与原项目 js/server-1.js 中的命令代码保持一致
 */
export enum DeviceCommand {
  // 状态查询命令
  GET_STATUS = 0x80,
  GET_DEVICE_DETAIL = 0x82,

  // EQ 命令（模块 0x52）
  EQ_MODULE = 0x52,
  EQ_SUBMODE_SINGLE = 0x02,  // 单点设置
  EQ_SUBMODE_LOGIN_STATUS = 0x13, // EQ登录状态同步（新增）
  EQ_SUBMODE_MODE = 0x14, // EQ开关、模式、标签及一键吃鸡联动状态
  EQ_SUBMODE_SEGMENT1 = 0x15, // 分段设置 - 第1段 (0-3频点)
  EQ_SUBMODE_SEGMENT2 = 0x16, // 分段设置 - 第2段 (4-7频点)
  EQ_SUBMODE_SEGMENT3 = 0x17, // 分段设置 - 第3段 (8-11频点)
  EQ_SUBMODE_SEGMENT4 = 0x18, // 分段设置 - 第4段 (12-13频点)

  // 静音命令
  SET_SPEAKER_MUTE = 0x54,  // 喇叭静音
  SET_MIC_MUTE = 0x55,      // 麦克风静音

  // 音量命令
  SET_SPEAKER_VOLUME = 0x56, // 喇叭音量
  SET_MIC_VOLUME = 0x57,       // 麦克风音量

  // 其他功能命令
  SET_ANTI_INTERFERENCE = 0x58, // 抗干扰
  SET_ENC = 0x51,               // AI降噪 (ENC)
  SET_RGB = 0x60,               // RGB灯效
  SET_VOICE_EFFECT = 0x61,      // 魔幻音效

  // 电源控制
  SET_POWER = 0x70,             // 关机/开机

  // 耳返/音频混合命令（0x5B 系列）
  SIDETONE_BASE = 0x5B,          // 耳返/音频混合基础命令
  SIDETONE_SUBMODE_BALANCE = 0x00,   // 子命令: 音频混合比例
  SIDETONE_SUBMODE_MODE = 0x01,      // 子命令: 音频模式切换 (Mix/Split/Adaptive)
  SIDETONE_SUBMODE_VOLUME = 0x02,    // 子命令: 耳返音量
  SIDETONE_SUBMODE_SWITCH = 0x03,    // 子命令: 耳返开关
  BT_SHARE_SUBMODE_SWITCH = 0x04,    // 子命令: 蓝牙音频分享
  CALL_BRIDGE_SUBMODE_SWITCH = 0x05, // 子命令: 多方通话桥接
}

// 帧头（AA 53）
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

/**
 * 计算协议帧校验和
 * 算法：累加帧头和所有数据字节后，取 16 位补码
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * 构建完整命令帧
 */
function buildFrame(command: number, data: number[] = []): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  // 例如 mute: AA 53 08 54 00 01 01 xx (总长 8)
  const length = data.length + 5 // 帧头(2) + 长度(1) + 命令(1) + 数据(n) + 校验和(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * 将 float32 转换为 4 个字节（小端序）
 * 用于编码 EQ 频率、增益和 Q 值
 */
function float32ToBytes(value: number): number[] {
  const buffer = new ArrayBuffer(4)
  const view = new DataView(buffer)
  view.setFloat32(0, value, true) // true 表示小端序
  return Array.from(new Uint8Array(buffer))
}

/**
 * 构建喇叭静音命令
 * 开: AA 53 08 54 00 01 01 a5
 * 关: AA 53 08 54 00 01 00 a6
 */
export function buildSpeakerMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_SPEAKER_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * 构建麦克风静音命令
 * 开: AA 53 08 55 00 01 01 a4
 * 关: AA 53 08 55 00 01 00 a5
 */
export function buildMicMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_MIC_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * 构建喇叭音量命令
 * 旧版：sendSliderValue，cmd2='56'
 * 格式：AA 53 08 56 00 01 [音量值] [校验和]
 * 音量范围：0-100
 */
export function buildSpeakerVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_SPEAKER_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * 构建麦克风音量命令
 * 旧版：sendSliderValue，cmd2='57'
 * 格式：AA 53 08 57 00 01 [音量值] [校验和]
 * 音量范围：0-100
 */
export function buildMicVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_MIC_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * 构建抗干扰命令
 * 开: AA 53 08 58 00 01 01 a1
 * 关: AA 53 08 58 00 01 00 a2
 */
export function buildAntiInterferenceCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_ANTI_INTERFERENCE, [0x00, 0x01, enabled ? 0x01 : 0x00])
}

/**
 * 构建 ENC（AI 降噪）命令
 * 旧版 server-1.js：AA 53 09 51 00 02 [开关] 0{档位} [校验和]
 * 档位范围：1-6（旧版为 1-6 档，Vue3 UI 需要保持一致）
 *
 * @param enabled - 开关状态
 * @param level - 档位 1-6
 */
export function buildEncCommand(enabled: boolean, level: number): Uint8Array {
  const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
  // 格式：长度=09，命令=51，00，02，[开关]，[档位]，校验和
  return buildFrame(DeviceCommand.SET_ENC, [0x00, 0x02, enabled ? 0x01 : 0x00, clampedLevel])
}

/**
 * 构建 RGB 灯效命令
 * 旧版格式：AA 53 08 60 01 01 [模式] [亮度]
 * 简化版本：通过颜色实现基础开关
 */
export function buildRgbCommand(enabled: boolean, mode: number = 0): Uint8Array {
  // 对齐旧版 server-1.js：AA 53 08 60 01 01 [模式] [校验和]
  // 备注：旧版协议该命令不带亮度字节
  return buildFrame(DeviceCommand.SET_RGB, [0x01, 0x01, enabled ? mode : 0x00])
}

/**
 * 构建电源开关命令
 * 关机: AA 53 08 70 00 01 00 8A
 * 开机: AA 53 08 70 00 01 01 89
 */
export function buildPowerCommand(powerOn: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_POWER, [0x00, 0x01, powerOn ? 0x01 : 0x00])
}

/**
 * 构建变声（魔幻音效）命令
 * 类型：0=正常，1=男声，2=女声，3=童声，4=机器人，5=怪兽
 */
export function buildVoiceEffectCommand(type: number): Uint8Array {
  return buildFrame(DeviceCommand.SET_VOICE_EFFECT, [0x00, 0x01, type])
}

/**
 * 构建耳返开关命令
 * 开启：AA 53 08 5B 03 01 01 [校验和]
 * 关闭：AA 53 08 5B 03 01 00 [校验和]
 */
export function buildSidetoneSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00
  ])
}

/**
 * 构建耳返音量命令
 * 格式：AA 53 08 5B 02 01 [音量:0-100] [校验和]
 */
export function buildSidetoneVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_VOLUME,
    0x01,
    clampedVolume
  ])
}

/**
 * 构建音频模式命令（音频模式切换：Mix/Split/Adaptive）
 * 格式：AA 53 08 5B 01 01 [模式] [校验和]
 * 无应答命令
 */
export function buildAudioModeCommand(mode: number): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_MODE,
    0x01,
    mode
  ])
}

/**
 * 构建音频平衡命令（音频混合比例）
 * 格式：AA 53 08 5B 00 01 [值] [校验和]
 * Position 值：0-100（UI），映射为 0-200（协议）
 * 最左 => 0, 中间 => 100, 最右 => 200
 */
export function buildAudioBalanceCommand(positionValue: number): Uint8Array {
  const sendValue = Math.max(0, Math.min(200, Math.round(positionValue * 2)))
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_BALANCE,
    0x01,
    sendValue
  ])
}

/** 一键吃鸡功能使用相同的 EQ 数据载荷，其中 0x15/0x16 保留给脚步声，
 * 0x17/0x18 保留给枪声（旧版 chijicode.js 映射）。 */
export function buildPositioningSegmentCommand(
  subModule: number,
  points: EqDataPoint[],
): Uint8Array {
  if (![0x15, 0x16, 0x17, 0x18].includes(subModule)) {
    throw new Error(`Invalid positioning submodule: ${subModule}`)
  }
  const payload: number[] = []
  for (const point of points) {
    const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
    payload.push(...float32ToBytes(freqValue))
    payload.push(...float32ToBytes(point.gain))
    payload.push(...float32ToBytes(parseFloat(point.q)))
  }
  // 旧版 zyg_build_packet 会在子模块后写入载荷长度。
  const totalLength = payload.length + 7
  const frameData = [totalLength, DeviceCommand.EQ_MODULE, subModule, payload.length, ...payload]
  const checksum = parseInt(calculateChecksum(frameData), 16)
  return new Uint8Array([0xAA, 0x53, ...frameData, checksum])
}

export function buildPositioningCurveCommands(
  footsteps: EqDataPoint[],
  gunshots: EqDataPoint[],
): Uint8Array[] {
  return [
    buildPositioningSegmentCommand(0x15, footsteps.slice(0, 4)),
    buildPositioningSegmentCommand(0x16, footsteps.slice(4)),
    buildPositioningSegmentCommand(0x17, gunshots.slice(0, 4)),
    buildPositioningSegmentCommand(0x18, gunshots.slice(4)),
  ]
}
/** 2.4G / 蓝牙音频分享（旧版协议：0x5B / 0x04）。 */
export function buildBtShareSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.BT_SHARE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00,
  ])
}

/** 多设备通话桥接（旧版协议：0x5B / 0x05）。 */
export function buildCallBridgeSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.CALL_BRIDGE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00,
  ])
}

/**
 * 构建 EQ 登录状态数据包（EQ 登录状态同步）
 * 格式：AA 53 09 52 13 02 [登录状态:0/1] 00 [校验和]
 * 每次连续下发 0x52 EQ 数据前，先通过该协议同步当前登录状态
 */
export function buildEqLoginStatusCommand(isLoggedIn: boolean): Uint8Array {
  const loginStatus = isLoggedIn ? 0x01 : 0x00
  return buildFrame(DeviceCommand.EQ_MODULE, [
    DeviceCommand.EQ_SUBMODE_LOGIN_STATUS,
    0x02,  // 数据长度
    loginStatus,
    0x00   // reserved
  ])
}

// ============ EQ 命令（复杂协议） ============

/**
 * EQ 数据点：单个频点数据
 * 与旧版 eq_setting.js 结构保持一致
 */
export interface EqDataPoint {
  fid: number      // 频点ID (1-14)
  freq: string     // 频率显示文本 (如 '25HZ', '1K')
  gain: number     // 增益值 (-12 到 +12 dB)
  q: string        // Q值 (如 '0.75')
}

/**
 * 构建单点 EQ 命令（实时单点调节）
 * Module 0x52, SubMode 0x02 + bandIndex
 * 单点格式：使用 float32 小端序
 *
 * @param bandIndex - 频段索引 (0-13)
 * @param point - EQ数据点
 */
export function buildEqSinglePointCommand(bandIndex: number, point: EqDataPoint): Uint8Array {
  const subModule = DeviceCommand.EQ_SUBMODE_SINGLE
  const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
  const qValue = parseFloat(point.q)

  // 数据：subModule(1) + bandIndex(1) + freq(4 字节 float32) + gain(4 字节 float32) + q(4 字节 float32)
  const data = [
    subModule,
    bandIndex,
    ...float32ToBytes(freqValue),
    ...float32ToBytes(point.gain),
    ...float32ToBytes(qValue),
  ]

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * 构建分段 EQ 命令（分段发送完整曲线）
 * 14段按4个segment分段发送: 0x15/0x16/0x17/0x18
 *
 * 分段映射：
 * - 0x15：索引 0-3（频点 0、1、2、3）
 * - 0x16：索引 4-7（频点 4、5、6、7）
 * - 0x17：索引 8-11（频点 8、9、10、11）
 * - 0x18：索引 12-13（频点 12、13）
 *
 * @param segmentIndex - 段索引 (0-3)
 * @param points - 该段的所有频点数据
 */
export function buildEqSegmentCommand(segmentIndex: number, points: EqDataPoint[]): Uint8Array {
  const subModules = [
    DeviceCommand.EQ_SUBMODE_SEGMENT1,
    DeviceCommand.EQ_SUBMODE_SEGMENT2,
    DeviceCommand.EQ_SUBMODE_SEGMENT3,
    DeviceCommand.EQ_SUBMODE_SEGMENT4,
  ]

  const subModule = subModules[segmentIndex]
  if (subModule === undefined) {
    throw new Error(`Invalid segment index: ${segmentIndex}`)
  }

  // 数据：subModule(1) + [freq(4) + gain(4) + q(4)] * pointCount
  const data: number[] = [subModule]

  for (const point of points) {
    const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
    const qValue = parseFloat(point.q)
    data.push(...float32ToBytes(freqValue))
    data.push(...float32ToBytes(point.gain))
    data.push(...float32ToBytes(qValue))
  }

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * 构建完整 EQ 曲线命令（全部 4 个分段）
 * 返回 4 个命令组成的数组，调用方应按顺序并间隔发送
 */
export function buildEqCurveCommands(allPoints: EqDataPoint[]): Uint8Array[] {
  // 将 14 个频点拆分为 4 个分段
  const segments = [
    allPoints.slice(0, 4),   // 索引 0-3
    allPoints.slice(4, 8),   // 索引 4-7
    allPoints.slice(8, 12),  // 索引 8-11
    allPoints.slice(12, 14), // 索引 12-13
  ]

  return segments.map((points, index) => buildEqSegmentCommand(index, points))
}

/**
 * 构建 EQ 开关/模式/标签命令。
 * AA 53 0C 52 14 05 B0 B1 B2 B3 B4 校验和
 * B0 为 EQ 开关，B1 为 EQ 模式，B2 为一键吃鸡开关，
 * B3 为 EQ 预设索引，B4 为一键吃鸡预设索引。
 */
export function buildEqModeCommand(
  enabled: boolean,
  mode: number,
  presetIndex = 0,
  positioningEnabled = false,
  positioningPresetIndex = 0,
): Uint8Array {
  const normalizedMode = Math.max(0, Math.min(2, Math.round(mode)))
  const normalizedPresetIndex = enabled
    ? Math.max(0, Math.min(31, Math.round(presetIndex)))
    : 0

  return buildFrame(DeviceCommand.EQ_MODULE, [
    DeviceCommand.EQ_SUBMODE_MODE,
    0x05,
    enabled ? 0x01 : 0x00,
    normalizedMode,
    positioningEnabled ? 0x01 : 0x00,
    normalizedPresetIndex,
    Math.max(0, Math.min(31, Math.round(positioningPresetIndex))),
  ])
}

/** 供只需要切换 EQ 的调用方使用的兼容辅助函数。 */
export function buildEqEnableCommand(enabled: boolean): Uint8Array {
  return buildEqModeCommand(enabled, 0)
}

// ============ 可直接使用的命令预设 ============

export const COMMAND_PRESETS = {
  // 喇叭静音
  SPEAKER_MUTE_ON: 'AA 53 08 54 00 01 01 A5',
  SPEAKER_MUTE_OFF: 'AA 53 08 54 00 01 00 A6',

  // 麦克风静音
  MIC_MUTE_ON: 'AA 53 08 55 00 01 01 A4',
  MIC_MUTE_OFF: 'AA 53 08 55 00 01 00 A5',

  // 抗干扰
  ANTI_INTERFERENCE_ON: 'AA 53 08 58 00 01 01 A1',
  ANTI_INTERFERENCE_OFF: 'AA 53 08 58 00 01 00 A2',

  // 状态查询
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const

/**
 * 将命令十六进制字符串解析为 Uint8Array
 */
export function parseCommandHex(hexString: string): Uint8Array {
  return hexStringToBytes(hexString)
}

/**
 * 验证命令格式
 */
export function validateCommand(data: Uint8Array): boolean {
  if (data.length < 8) return false
  if (data[0] !== FRAME_HEADER[0] || data[1] !== FRAME_HEADER[1]) return false

  // 验证校验和
  const payload = Array.from(data.slice(2, -1))
  const receivedChecksum = data[data.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)

  return receivedChecksum === calculatedChecksum
}

// ============ EQ 登录状态辅助函数 ============

/**
 * 判断命令是否为 EQ 数据命令（需要在发送前注入登录状态）
 * @param hexString - 十六进制命令字符串（如 "AA 53 0E 52 02..."）
 * @returns true：如果是 EQ 数据命令（module=0x52 且 subModule!=0x13）
 */
export function isEqDataCommand(hexString: string): boolean {
  const normalized = String(hexString || '').replace(/\s+/g, '').toUpperCase()
  if (normalized.length < 10) return false

  const module = parseInt(normalized.slice(6, 8), 16)
  const subModule = parseInt(normalized.slice(8, 10), 16)

  return module === DeviceCommand.EQ_MODULE
    && subModule !== DeviceCommand.EQ_SUBMODE_LOGIN_STATUS
}

/**
 * 判断命令是否为 RGB 命令
 * @param hexString - 十六进制命令字符串
 * @returns true：如果是 RGB 命令（0x60）
 */
export function isRgbCommand(hexString: string): boolean {
  const normalized = String(hexString || '').replace(/\s+/g, '').toUpperCase()
  if (normalized.length < 8) return false

  const command = parseInt(normalized.slice(6, 8), 16)
  return command === DeviceCommand.SET_RGB
}

/**
 * 将 Uint8Array 转换为十六进制字符串（用于日志和判断）
 * @param bytes - 字节数组
 * @returns 十六进制字符串（空格分隔，大写）
 */
export function bytesToHexString(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

/**
 * EQ 登录状态同步控制器
 * 用于避免在同一连续 EQ 下发过程中重复发送登录状态
 */
export class EqLoginStatusSyncController {
  private lastDevice: any = null
  private lastStatus: number | null = null
  private lastTime: number = 0
  private readonly syncWindowMs: number = 1000 // 1秒内同一设备不重复发送

  /**
   * 判断是否需要同步 EQ 登录状态
   */
  shouldSync(device: any, loginStatus: number): boolean {
    const now = Date.now()
    return (
      this.lastDevice !== device ||
      this.lastStatus !== loginStatus ||
      now - this.lastTime >= this.syncWindowMs
    )
  }

  /**
   * 标记已同步
   */
  markSynced(device: any, loginStatus: number): void {
    this.lastDevice = device
    this.lastStatus = loginStatus
    this.lastTime = Date.now()
  }

  /**
   * 重置同步状态（设备断开时调用）
   */
  reset(): void {
    this.lastDevice = null
    this.lastStatus = null
    this.lastTime = 0
  }
}
